import React, { FunctionComponent, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import * as S from './styled'
import useConnectRequest from '../../hooks/useConnectRequest'
import { FETCH_ALL_PRODUCT_DATA_BY_USER } from '../../redux/requests/products/product.actions'
import { GET_USER_BY_UID } from '../../redux/requests/auth/auth.actions'
import { requestActions } from '../../redux/requests'
import { DocumentCard } from '../../components/molecules/DocumentCard/DocumentCard'
import UploadCard from '../../components/molecules/UploadCard/UploadCard'
import MyDropzone from '../../components/molecules/Dropzone/Dropzone'
import { BeatLoader } from 'react-spinners'
import { css } from '@emotion/react'
import Alert, { AlertType } from '../../components/atoms/Alert/Alert'
import PendingCard from '../../components/molecules/DocumentCard/PendingCard'
import { randomID } from '../../redux/misc/helpers'
import DocumentUtilsBar from '../../components/molecules/DocumentUtilsBar'
import { ListCard } from '../../components/molecules/DocumentCard/styled'
import { Label1 } from '../../theme/typography'
import { SortBy } from '../../components/molecules/DocumentSort/DocumentSort'
import moment from 'moment'
import { ProductData } from '../../redux/requests/products/product.types'

type OwnProps = {
  uid: string
}

type Props = OwnProps

const override = css`
  display: flex;
  margin: 0 7px;
  border-color: red;
`

const DocumentScreen: FunctionComponent<Props> = ({ uid }) => {
  const dispatch = useDispatch()
  let { data, loading, error } = useConnectRequest(FETCH_ALL_PRODUCT_DATA_BY_USER)
  const { data: user, error: errorUser } = useConnectRequest(GET_USER_BY_UID)

  const [dataState, setDataState] = useState(data)
  const [hasUploaded, setHasUploaded] = useState(false)
  const [fileIsDuplicate, setFileIsDuplicate] = useState(false)
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([])
  const [duplicatedFiles, setDuplicatedFiles] = useState<string[]>([])
  const [files, setFiles] = useState<File[]>([])
  const [numPendingCards, setNumPendingCards] = useState<number[]>([])
  const [listView, setListView] = useState(true)
  const [search, setSearch] = useState('')
  const [filteredData, setFilteredData] = useState(dataState)
  const [sortBy, setSortBy] = useState(SortBy.SETTLEMENTDATE_DESCENDING)
  const [sortedData, setSortedData] = useState<ProductData[]>(filteredData)

  useEffect(() => {
    dispatch(requestActions.products.fetchAllProductDataByUser(uid))
    dispatch(requestActions.auth.getUserByUid(uid))
    return () => {
      localStorage.removeItem('pendingDeletion')
    }
  }, [])

  function uploadDocuments() {
    setNumPendingCards(files.map(() => randomID()))
    setHasUploaded(true)

    for (let file of files) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('From', user.ownEmail!)
      formData.append('To', user.email)

      dispatch(
        requestActions.products.postAndUpdate(
          formData,
          uid,
          dispatch,
          setNumPendingCards,
          setFileIsDuplicate,
          setDuplicatedFiles,
        ),
      )
    }
    setFileIsDuplicate(false)
    setFiles([])
  }

  const sortConfig = {
    [SortBy.FILENAME_ASCENDING]: (a, b) => a.fileName!.localeCompare(b.fileName!, 'sv'),
    [SortBy.FILENAME_DESCENDING]: (a, b) => b.fileName!.localeCompare(a.fileName!, 'sv'),
    [SortBy.UPLOADEDBY_ASCENDING]: (a, b) => a.uploadedBy!.localeCompare(b.uploadedBy!),
    [SortBy.UPLOADEDBY_DESCENDING]: (a, b) => b.uploadedBy!.localeCompare(a.uploadedBy!),
    [SortBy.SETTLEMENTDATE_ASCENDING]: (a, b) =>
      +new Date(a.settlementDate) - +new Date(b.settlementDate),
    [SortBy.SETTLEMENTDATE_DESCENDING]: (a, b) =>
      +new Date(b.settlementDate) - +new Date(a.settlementDate),
    [SortBy.UPLOADDATE_ASCENDING]: (a, b) => {
      return (
        moment(a.date, 'YYYY-MM-DD HH:mm:ss').valueOf() -
        moment(b.date, 'YYYY-MM-DD HH:mm:ss').valueOf()
      )
    },
    [SortBy.UPLOADDATE_DESCENDING]: (a, b) =>
      moment(b.date, 'YYYY-MM-DD HH:mm:ss').valueOf() -
      moment(a.date, 'YYYY-MM-DD HH:mm:ss').valueOf(),
  }

  useEffect(() => {
    setSortedData([...filteredData].sort(sortConfig[sortBy]))
  }, [sortBy, filteredData])

  useEffect(() => {
    if (numPendingCards.length < 2) {
      setHasUploaded(false)
    }
    setDataState(data)
  }, [data])

  useEffect(() => {
    data = dataState
  }, [dataState])

  useEffect(() => {
    const pendingDeletion: string | null = localStorage.getItem('pendingDeletion')
    let pendingDeletionParse: string[] = []
    if (pendingDeletion !== null) {
      pendingDeletionParse = JSON.parse(pendingDeletion)
    }

    if (pendingDeletionParse.length > 0) {
      setDataState(data.filter(i => !pendingDeletionParse.includes(i.id)))
    }

    let toDelete: string[] = []
    for (let p of pendingDeletionParse) {
      if (!data.map(i => i.id).some(j => j === p)) {
        toDelete.push(p)
      }
    }
  }, [data])

  useEffect(() => {
    if (search.length > 0) {
      const searchResults = dataState.filter(
        item =>
          item.fileName!.toLowerCase().includes(search.toLowerCase()) ||
          item.uploadedBy!.toLowerCase().includes(search.toLowerCase()) ||
          item.date!.toLowerCase().includes(search.toLowerCase()),
      )
      setFilteredData(searchResults)
    } else {
      setFilteredData(dataState)
    }
  }, [search, dataState])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFileIsDuplicate(false)
      setDuplicatedFiles([])
    }, 3500)
    return () => {
      clearTimeout(timeout)
    }
  }, [fileIsDuplicate])

  return (
    <div>
      {dataState.length < 1 && !hasUploaded && (
        <Alert type={AlertType.Warning} message={'Ingen data tillgänglig'} />
      )}
      {hasUploaded && <Alert type={AlertType.Info} message={'Uppladdningen pågår'} />}
      {fileIsDuplicate && (
        <Alert
          type={AlertType.Error}
          message={`Filen finns redan! Filer: ${
            rejectedFiles.length > 0 ? rejectedFiles.join(', ') : duplicatedFiles.join(', ')
          }`}
        />
      )}
      <S.ScreenWrapper>
        <DocumentUtilsBar
          listView={listView}
          setListView={setListView}
          setSearch={setSearch}
          setSortBy={setSortBy}
        />
        <S.CardContainer listView={listView}>
          {files.length < 1 && (
            <MyDropzone
              documentView
              files={files}
              setFiles={setFiles}
              setFileIsDuplicate={setFileIsDuplicate}
              setRejectedFiles={setRejectedFiles}
              listView={listView}
            />
          )}
          {(error || errorUser) && <p>Något gick fel</p>}
          {files.length > 0 && (
            <S.ExpandedDropZone>
              <MyDropzone
                documentView
                files={files}
                setFiles={setFiles}
                setFileIsDuplicate={setFileIsDuplicate}
                setRejectedFiles={setRejectedFiles}
                listView={listView}
              />
              <UploadCard
                files={files}
                setFiles={setFiles}
                user={user}
                setHasUploaded={setHasUploaded}
                hasUploaded={hasUploaded}
                uploadDocuments={uploadDocuments}
              />
            </S.ExpandedDropZone>
          )}
          {listView && (
            <ListCard noBackground>
              <Label1>Filnamn</Label1>
              <Label1>Produkt</Label1>
              <Label1>Uppladdat av</Label1>
              <Label1>Avräkningsdatum</Label1>
            </ListCard>
          )}
          {sortedData.length > 0
            ? sortedData.map(doc => (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  data={dataState}
                  setDataState={setDataState}
                  listView={listView}
                />
              ))
            : 'Här var det tomt!'}
          {numPendingCards.length > 0 &&
            numPendingCards.map(num => <PendingCard key={num} listView={listView} />)}
          <BeatLoader css={override} loading={loading} size={15} />
        </S.CardContainer>
      </S.ScreenWrapper>
    </div>
  )
}

export default DocumentScreen
